import type { ProductCounterProps } from './data';

import { Minus, Plus, Trash2 } from 'react-feather';
import { useShallow } from 'zustand/react/shallow';

import { Box, Button, Icon, InventoryModal } from '@/components';
import { useBasketStore, useDisclosure, useTranslation } from '@/hooks';
import { productCount } from '@/utils';
import { googleEventRemoveFromCart } from '@/utils/googleAnalyticsEvent';

const ProductCounter: ProductCounterProps = ({
  product,
  selectedVariantCode,
  onNotSelectedVariant,
  isLoading,
}) => {
  const { t } = useTranslation('pages.products');
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [count, decrease, increase, remove] = useBasketStore(
    useShallow((state) => [
      productCount({ products: state.products, code: product.code }),
      state.decrease,
      state.increase,
      state.remove,
    ])
  );

  const isExpanded = count > 0;
  const isViewProduct = product.type === 'view';
  const isOutOfStock = !(product.inventory.is_available || isViewProduct);

  return (
    <>
      {isExpanded ? (
        <Button
          color="primary.500"
          bg="grayAlpha.200"
          _hover={{ bg: 'grayAlpha.200' }}
          py="0"
          paddingInline={{ base: '4px !important', md: '0' }}
          paddingBlock={{ base: '2', md: '0' }}
          size={{ base: 'lg', md: 'xl' }}
          fontSize={{ base: 'lg', md: '30px' }}
          leftIcon={
            <Icon
              width={{ base: '4', md: '6' }}
              height={{ base: '4', md: '6' }}
              my={{ base: '2', md: '3' }}
              ml={{ base: '3', md: '22px' }}
              mr={{ base: '2', md: '4' }}
              as={Plus}
              opacity={
                count < product.inventory.limit_buy_inventory || isViewProduct
                  ? 1
                  : 0.3
              }
              onClick={() => increase({ code: product.code })}
            />
          }
          rightIcon={
            <Icon
              width={{ base: '4', md: '6' }}
              height={{ base: '4', md: '6' }}
              as={count === 1 ? Trash2 : Minus}
              mr={{ base: '3', md: '22px' }}
              ml={{ base: '2', md: '4' }}
              my={{ base: '2', md: '3' }}
              onClick={() => {
                if (count === 1) {
                  googleEventRemoveFromCart({
                    id: product.id,
                    title: product.title,
                    price: product.price,
                    payable_price: product.pricing
                      ? product.pricing.payable_price
                      : product.price,
                    quantity: count,
                  });
                  remove({ code: product.code });
                  return;
                }
                decrease({ code: product.code });
              }}
            />
          }
        >
          <Box width="40px">{count}</Box>
        </Button>
      ) : (
        <Button
          justifyContent="center"
          type="submit"
          isDisabled={isOutOfStock}
          isLoading={isLoading}
          onClick={() => {
            if (isLoading) {
              return;
            }
            if (isViewProduct && !selectedVariantCode) {
              onNotSelectedVariant();
            } else {
              onOpen();
            }
          }}
        >
          {isOutOfStock ? t('outOfStock') : t('addToBasket')}
        </Button>
      )}
      {isOpen && (
        <InventoryModal onClose={onClose} isOpen={isOpen} product={product} />
      )}
    </>
  );
};
export default ProductCounter;
